/**
 * Motor Neutro **D1** — o que a superfície de AUTORIA precisa saber para não mentir.
 *
 * Duas perguntas que a tela de contatos autorizados fazia errado, e que aqui se respondem
 * pela MESMA fonte que o motor usa:
 *
 * 1. **«Este escopo gateia alguma coisa?»** A plataforma compara escopo por **igualdade de
 *    chave**, e a C1 mediu que os vocabulários não se conhecem: das **21** chaves que o
 *    `ScopePicker` oferece, **16** não são exigidas por ação alguma. O operador marcava a caixa
 *    e a tela confirmava sucesso. A derivação é do `@lume/shared` (`collectRequiredScopes` /
 *    `isScopeEffective`) — **nunca** uma segunda lista mantida no frontend, que seria criar mais
 *    um vocabulário a divergir.
 *
 * 2. **«Este mandato ainda vale?»** O gate honra a caducidade desde a B6 (polaridade
 *    `expires-forfeits`), mas resolve em tempo de LEITURA: **nenhum writer** escreve
 *    `status='expired'` no banco (R-B6-b — o forfeit em leitura é o conserto correto). Logo o
 *    card exibia **«Ativo»** para mandato vencido. Aqui o estado é derivado por
 *    `resolveVoluntaryGrant` — **a função do próprio caminho de autorização** — e não por uma
 *    comparação de datas reescrita no FE (seria a 4ª ponta divergente do mesmo eixo).
 *
 * ⚠️ **Fronteira declarada (spec §4 item 6 / premissa P10).** O conjunto exigido é derivado do
 * **seed de código**, o que hoje é a verdade do servidor porque o override por-tenant da C1
 * (`resolveActionScope({ override })`) é **DORMENTE: zero call-sites o passam**. Quando a autoria
 * por-tenant existir, o conjunto tem de vir do SERVIDOR — o guard
 * `scope-effectiveness.frontier.spec.ts` fica vermelho nesse dia e diz isto.
 *
 * ⚠️ A claim é **GLOBAL e conservadora**: «nenhuma ação, em nenhum vertical conhecido, exige esta
 * chave». NÃO afirma «este mandato é inútil neste vínculo» — isso exigiria resolver o vertical da
 * conversa, e seria claim maior que a medição (C1 §3.3).
 *
 * Puro, sem I/O, sem lançar. Informacional: **nada aqui nega** — quem decide é o servidor.
 *
 * Spec: docs/superpowers/specs/2026-07-26-frente-d1-superficie-operador-escopo-validade-design.md
 */

import { collectRequiredScopes, isScopeEffective, resolveVoluntaryGrant } from '@lume/shared';

/** Cache do conjunto exigido — a derivação é pura e o seed não muda em runtime. */
let requiredCache: ReadonlySet<string> | null = null;

function requiredScopes(): ReadonlySet<string> {
  requiredCache ??= collectRequiredScopes();
  return requiredCache;
}

/**
 * `true` quando a chave **não é exigida por nenhuma ação em nenhum vertical conhecido** — ou
 * seja, conceder este escopo não muda o comportamento do motor hoje.
 */
export function isScopeInert(scope: string): boolean {
  return !isScopeEffective(scope, requiredScopes());
}

/** As chaves inertes de uma lista concedida, na ordem em que foram concedidas. */
export function inertScopesOf(scopes: readonly string[]): string[] {
  return scopes.filter((s) => isScopeInert(s));
}

/**
 * O estado do mandato **como o motor o vê**. `'expired'` só existe aqui: no banco a coluna
 * segue `'active'` (R-B6-b).
 */
export type MandateValidity = 'active' | 'revoked' | 'expired';

/**
 * Deriva o estado do mandato pela função do caminho de autorização (`resolveVoluntaryGrant`).
 *
 * Herda dela o fail-safe com DIREÇÃO: `expiresAt` corrompido ⇒ **caduca** (nunca concede).
 */
export function deriveMandateValidity(
  contact: {
    readonly id: string;
    readonly personId: string;
    readonly scopes: readonly string[];
    readonly status: string;
    readonly expiresAt: string | null;
    readonly createdAt?: string | null;
  },
  now: string = new Date().toISOString(),
): MandateValidity {
  if (contact.status === 'revoked') return 'revoked';
  if (contact.status !== 'active') return contact.status === 'expired' ? 'expired' : 'active';

  const resolution = resolveVoluntaryGrant({
    grants: [
      {
        grantId: contact.id,
        granteePersonId: contact.personId,
        scopes: contact.scopes,
        status: 'active',
        expiresAt: contact.expiresAt,
        createdAt: contact.createdAt ?? null,
      },
    ],
    invokerPersonId: contact.personId,
    now,
  });

  return resolution.forfeited ? 'expired' : 'active';
}
